/**
 * CMS Production Theme Presets — Mix & Match Design System
 * 
 * Each preset is a saved combination of interchangeable design layers.
 * Users start from a preset and can customize any individual layer.
 */

// ─── Layer Option Types ───

export type HeaderStyle = 'bold' | 'centered' | 'minimal' | 'split' | 'mega';
export type HeroVariant = 'full-bleed' | 'split' | 'video' | 'slider' | 'minimal' | 'stacked';
export type CardStyle = 'elevated' | 'flat' | 'bordered' | 'glass' | 'minimal';
export type ColorPalette = 
  | 'brand' | 'brand-teal' | 'brand-gold' | 'brand-coral' 
  | 'brand-emerald' | 'brand-violet' | 'brand-sky' | 'brand-rose'
  | 'brand-amber' | 'brand-slate';
export type TypographyPack = 'strong' | 'elegant' | 'modern' | 'industrial' | 'friendly';
export type FooterStyle = 'mega' | 'compact' | 'centered' | 'split' | 'minimal';
export type TestimonialStyle = 'carousel' | 'grid' | 'featured' | 'marquee';
export type CtaStyle = 'banner' | 'floating' | 'inline' | 'split';

// ─── Design System ───
export type DesignSystem = 'modern' | 'glassmorphism' | 'minimal' | 'neumorphic';

export interface DesignSystemTokens {
  '--ds-radius': string;
  '--ds-shadow': string;
  '--ds-shadow-hover': string;
  '--ds-border': string;
  '--ds-blur': string;
  '--ds-surface': string;
  '--ds-surface-hover': string;
  '--ds-inset-shadow': string;
}

export interface DesignSystemPreset {
  id: DesignSystem;
  name: string;
  tagline: string;
  description: string;
  thumbnail_gradient: string;
  tokens: DesignSystemTokens;
}

export const DESIGN_SYSTEM_PRESETS: DesignSystemPreset[] = [
  {
    id: 'modern',
    name: 'Modern',
    tagline: 'Clean & contemporary',
    description: 'Standard rounded corners, subtle shadows, solid surfaces. The default design language — versatile and professional.',
    thumbnail_gradient: 'linear-gradient(135deg, hsl(220 14% 96%) 0%, hsl(0 0% 100%) 50%, hsl(220 14% 96%) 100%)',
    tokens: {
      '--ds-radius': '0.625rem',
      '--ds-shadow': '0 1px 3px rgba(0,0,0,0.06)',
      '--ds-shadow-hover': '0 4px 12px rgba(0,0,0,0.1)',
      '--ds-border': '1px solid hsl(var(--border))',
      '--ds-blur': 'none',
      '--ds-surface': 'hsl(var(--card))',
      '--ds-surface-hover': 'hsl(var(--muted))',
      '--ds-inset-shadow': 'none',
    },
  },
  {
    id: 'glassmorphism',
    name: 'Glassmorphism',
    tagline: 'Frosted glass elegance',
    description: 'Translucent surfaces with frosted glass blur, soft diffused shadows, and luminous glass-edge borders. Premium and futuristic.',
    thumbnail_gradient: 'linear-gradient(135deg, rgba(255,255,255,0.4) 0%, rgba(200,210,255,0.3) 50%, rgba(255,255,255,0.2) 100%)',
    tokens: {
      '--ds-radius': '1rem',
      '--ds-shadow': '0 8px 32px rgba(0,0,0,0.08)',
      '--ds-shadow-hover': '0 12px 40px rgba(0,0,0,0.12)',
      '--ds-border': '1px solid rgba(255,255,255,0.18)',
      '--ds-blur': 'blur(20px)',
      '--ds-surface': 'rgba(255,255,255,0.7)',
      '--ds-surface-hover': 'rgba(255,255,255,0.85)',
      '--ds-inset-shadow': 'inset 0 1px 0 rgba(255,255,255,0.2)',
    },
  },
  {
    id: 'minimal',
    name: 'Minimal',
    tagline: 'Less is more',
    description: 'Completely flat, no shadows, barely-rounded corners, precise thin borders. Maximum whitespace and content focus.',
    thumbnail_gradient: 'linear-gradient(135deg, hsl(0 0% 100%) 0%, hsl(0 0% 98%) 100%)',
    tokens: {
      '--ds-radius': '0.25rem',
      '--ds-shadow': 'none',
      '--ds-shadow-hover': 'none',
      '--ds-border': '1px solid hsl(var(--border))',
      '--ds-blur': 'none',
      '--ds-surface': 'hsl(var(--card))',
      '--ds-surface-hover': 'hsl(var(--muted))',
      '--ds-inset-shadow': 'none',
    },
  },
  {
    id: 'neumorphic',
    name: 'Neumorphic',
    tagline: 'Soft extruded surfaces',
    description: 'Soft dual-shadow extrusions, no visible borders, rounded shapes that appear to push out from the background. Tactile and modern.',
    thumbnail_gradient: 'linear-gradient(145deg, hsl(0 0% 93%) 0%, hsl(0 0% 98%) 50%, hsl(0 0% 93%) 100%)',
    tokens: {
      '--ds-radius': '1rem',
      '--ds-shadow': '6px 6px 16px rgba(0,0,0,0.08), -6px -6px 16px rgba(255,255,255,0.9)',
      '--ds-shadow-hover': '8px 8px 20px rgba(0,0,0,0.1), -8px -8px 20px rgba(255,255,255,1)',
      '--ds-border': 'none',
      '--ds-blur': 'none',
      '--ds-surface': 'hsl(var(--muted))',
      '--ds-surface-hover': 'hsl(var(--muted))',
      '--ds-inset-shadow': 'inset 2px 2px 4px rgba(0,0,0,0.06), inset -2px -2px 4px rgba(255,255,255,0.7)',
    },
  },
];

export const getDesignSystemPreset = (id: DesignSystem): DesignSystemPreset =>
  DESIGN_SYSTEM_PRESETS.find(p => p.id === id) || DESIGN_SYSTEM_PRESETS[0];

export interface BrandColors {
  primary: string;   // HSL string e.g. "210 50% 40%"
  secondary: string;
  accent: string;
}

export interface CmsThemeConfig {
  active_preset: string | null;
  design_system: DesignSystem;
  header_style: HeaderStyle;
  hero_variant: HeroVariant;
  card_style: CardStyle;
  color_palette: ColorPalette;
  typography_pack: TypographyPack;
  footer_style: FooterStyle;
  section_card_style: CardStyle;
  testimonial_style: TestimonialStyle;
  cta_style: CtaStyle;
  custom_colors?: BrandColors | null;
  custom_fonts?: {
    heading: string;
    body: string;
    headingFileUrl?: string | null;
    bodyFileUrl?: string | null;
  } | null;
}

export interface ThemePreset {
  id: string;
  name: string;
  tagline: string;
  description: string;
  category: 'aesthetic' | 'conversion' | 'industry';
  thumbnail_gradient: string;
  accent_color: string;
  config: CmsThemeConfig;
}

// ─── Layer Options with Labels ───

export const HEADER_STYLES: { value: HeaderStyle; label: string; description: string }[] = [
  { value: 'bold', label: 'Bold Bar', description: 'Strong top bar with prominent CTA and phone number' },
  { value: 'centered', label: 'Centered Logo', description: 'Logo centered with nav underneath' },
  { value: 'minimal', label: 'Minimal', description: 'Clean single-line with subtle navigation' },
  { value: 'split', label: 'Split Bar', description: 'Logo left, nav center, CTA right' },
  { value: 'mega', label: 'Mega Header', description: 'Full mega menu with service categories dropdown' },
];

export const HERO_VARIANTS: { value: HeroVariant; label: string; description: string }[] = [
  { value: 'full-bleed', label: 'Full Bleed', description: 'Edge-to-edge hero image with overlay' },
  { value: 'split', label: 'Split Layout', description: 'Text left, image/form right' },
  { value: 'video', label: 'Video Background', description: 'Auto-playing background video with overlay' },
  { value: 'slider', label: 'Image Slider', description: 'Rotating hero images with transitions' },
  { value: 'minimal', label: 'Minimal Text', description: 'Clean text-focused hero with subtle background' },
  { value: 'stacked', label: 'Stacked', description: 'Heading, subtext, CTA stacked vertically with reviews' },
];

export const CARD_STYLES: { value: CardStyle; label: string; description: string }[] = [
  { value: 'elevated', label: 'Elevated', description: 'Cards with shadow depth and hover lift' },
  { value: 'flat', label: 'Flat', description: 'No shadow, clean backgrounds' },
  { value: 'bordered', label: 'Bordered', description: 'Defined borders with subtle backgrounds' },
  { value: 'glass', label: 'Glassmorphism', description: 'Frosted glass effect with blur' },
  { value: 'minimal', label: 'Minimal', description: 'Ultra-clean with maximum whitespace' },
];

// Accent variations to pair with logo-extracted primary/secondary
export const ACCENT_VARIATIONS: { value: ColorPalette; label: string; description: string; accentHsl: string }[] = [
  { value: 'brand', label: 'Original Brand', description: 'Your logo colors as extracted', accentHsl: '' },
  { value: 'brand-teal', label: 'Teal Accent', description: 'Fresh teal accent — clean & modern', accentHsl: '174 72% 44%' },
  { value: 'brand-gold', label: 'Gold Accent', description: 'Warm gold accent — premium & trusted', accentHsl: '43 96% 56%' },
  { value: 'brand-coral', label: 'Coral Accent', description: 'Energetic coral accent — bold & inviting', accentHsl: '12 76% 61%' },
  { value: 'brand-emerald', label: 'Emerald Accent', description: 'Rich emerald accent — growth & reliability', accentHsl: '152 69% 40%' },
  { value: 'brand-violet', label: 'Violet Accent', description: 'Violet accent — creative & distinctive', accentHsl: '270 67% 55%' },
  { value: 'brand-sky', label: 'Sky Blue Accent', description: 'Bright sky accent — open & approachable', accentHsl: '199 89% 48%' },
  { value: 'brand-rose', label: 'Rose Accent', description: 'Warm rose accent — friendly & welcoming', accentHsl: '346 77% 55%' },
  { value: 'brand-amber', label: 'Amber Accent', description: 'Rich amber accent — warm & dependable', accentHsl: '26 90% 50%' },
  { value: 'brand-slate', label: 'Slate Accent', description: 'Neutral slate accent — professional & understated', accentHsl: '215 20% 45%' },
];

export const COLOR_PALETTES: { value: ColorPalette; label: string; description: string; preview: string[] }[] = ACCENT_VARIATIONS.map(v => ({
  value: v.value,
  label: v.label,
  description: v.description,
  preview: ['#6366F1', '#8B5CF6', '#A78BFA'], // placeholders, overridden dynamically
}));

export const TYPOGRAPHY_PACKS: { value: TypographyPack; label: string; heading: string; body: string }[] = [
  { value: 'strong', label: 'Strong Impact', heading: 'Oswald', body: 'Open Sans' },
  { value: 'elegant', label: 'Elegant Pro', heading: 'Playfair Display', body: 'Lato' },
  { value: 'modern', label: 'Modern Clean', heading: 'Plus Jakarta Sans', body: 'Inter' },
  { value: 'industrial', label: 'Industrial', heading: 'Bebas Neue', body: 'Roboto' },
  { value: 'friendly', label: 'Friendly', heading: 'Nunito', body: 'Source Sans 3' },
];

export const FOOTER_STYLES: { value: FooterStyle; label: string; description: string }[] = [
  { value: 'mega', label: 'Mega Footer', description: 'Full 4-column footer with map, services, areas, contact' },
  { value: 'compact', label: 'Compact', description: 'Single row with essential links' },
  { value: 'centered', label: 'Centered', description: 'Logo centered with stacked links' },
  { value: 'split', label: 'Split', description: 'CTA left, links right' },
  { value: 'minimal', label: 'Minimal', description: 'Just copyright and social icons' },
];

export const TESTIMONIAL_STYLES: { value: TestimonialStyle; label: string }[] = [
  { value: 'carousel', label: 'Carousel Slider' },
  { value: 'grid', label: 'Card Grid' },
  { value: 'featured', label: 'Featured + List' },
  { value: 'marquee', label: 'Auto-Scroll Marquee' },
];

export const CTA_STYLES: { value: CtaStyle; label: string }[] = [
  { value: 'banner', label: 'Full-Width Banner' },
  { value: 'floating', label: 'Floating Bar' },
  { value: 'inline', label: 'Inline Sections' },
  { value: 'split', label: 'Split CTA' },
];

// ─── The 3 Starter Presets ───

export const THEME_PRESETS: ThemePreset[] = [
  {
    id: 'bold-contractor',
    name: 'Bold Contractor',
    tagline: 'High-impact design that demands attention',
    description: 'Dark header, full-bleed hero with gradient overlay, bold orange accent, strong shadows. Conversion-heavy with trust badges and review counters everywhere. Perfect for contractors who want to stand out.',
    category: 'conversion',
    thumbnail_gradient: 'linear-gradient(135deg, #F97316 0%, #1C1917 60%, #292524 100%)',
    accent_color: '#F97316',
    config: {
      active_preset: 'bold-contractor',
      design_system: 'modern',
      header_style: 'bold',
      hero_variant: 'full-bleed',
      card_style: 'elevated',
      color_palette: 'brand-amber',
      typography_pack: 'strong',
      footer_style: 'mega',
      section_card_style: 'elevated',
      testimonial_style: 'carousel',
      cta_style: 'banner',
    },
  },
  {
    id: 'clean-professional',
    name: 'Clean Professional',
    tagline: 'Refined elegance that builds authority',
    description: 'Light minimal header, split hero, blue-gray palette, lots of whitespace. Authority-focused layout emphasizing credentials, certifications, and team. Ideal for established companies.',
    category: 'aesthetic',
    thumbnail_gradient: 'linear-gradient(135deg, #2563EB 0%, #F8FAFC 40%, #E2E8F0 100%)',
    accent_color: '#2563EB',
    config: {
      active_preset: 'clean-professional',
      design_system: 'minimal',
      header_style: 'minimal',
      hero_variant: 'split',
      card_style: 'minimal',
      color_palette: 'brand-sky',
      typography_pack: 'modern',
      footer_style: 'split',
      section_card_style: 'flat',
      testimonial_style: 'featured',
      cta_style: 'inline',
    },
  },
  {
    id: 'local-expert',
    name: 'Local Expert',
    tagline: 'Community-rooted design that converts locally',
    description: 'Warm earthy tones, stacked hero with location emphasis, area-focused layout. Service area coverage and deal/promo sections are prominent. Built for dominating local search.',
    category: 'industry',
    thumbnail_gradient: 'linear-gradient(135deg, #92400E 0%, #365314 40%, #FEF3C7 100%)',
    accent_color: '#92400E',
    config: {
      active_preset: 'local-expert',
      design_system: 'modern',
      header_style: 'mega',
      hero_variant: 'stacked',
      card_style: 'bordered',
      color_palette: 'brand-emerald',
      typography_pack: 'friendly',
      footer_style: 'mega',
      section_card_style: 'bordered',
      testimonial_style: 'grid',
      cta_style: 'split',
    },
  },
];

export const DEFAULT_THEME_CONFIG: CmsThemeConfig = THEME_PRESETS[0].config;

export const getPresetById = (id: string): ThemePreset | undefined => 
  THEME_PRESETS.find(p => p.id === id);
